import React, { useMemo } from 'react';
import { buildDireccionResumen } from './FichaOdontologicaForm';
import { sanitizeContactosEmergencia } from '../../data/consultorioData';

const EMPTY = 'Sin registrar';

function calcularEdad(fechaNac) {
  if (!fechaNac) return null;
  const hoy = new Date();
  const nac = new Date(fechaNac);
  let edad = hoy.getFullYear() - nac.getFullYear();
  if (hoy.getMonth() < nac.getMonth() || (hoy.getMonth() === nac.getMonth() && hoy.getDate() < nac.getDate())) {
    edad--;
  }
  return edad;
}

function sexoLabel(sexo) {
  const map = { M: 'Masculino', F: 'Femenino', Otro: 'Otro', ND: 'Prefiero no decirlo' };
  return map[sexo] || (sexo || '').trim() || '';
}

function hasValue(v) {
  if (v === undefined || v === null) return false;
  return String(v).trim() !== '';
}

function contactosEmergenciaVista(form) {
  const rows = sanitizeContactosEmergencia(form.contactosEmergencia).filter(
    (r) => !!(r?.nombre || r?.telefono || r?.relacion)
  );
  if (rows.length) return rows;
  const legacy = {
    nombre: (form.contactoEmergencia || '').trim(),
    telefono: (form.telefonoEmergencia || '').trim(),
    relacion: (form.contactoEmergenciaRelacion || '').trim(),
  };
  return legacy.nombre || legacy.telefono || legacy.relacion ? [legacy] : [];
}

function formatFecha(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-EC', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(iso);
  }
}

const TRACK_KEYS = [
  'nombre',
  'tipoIdentificacion',
  'cedula',
  'fechaNacimiento',
  'sexo',
  'telefono',
  'email',
  'direccionProvincia',
  'direccionExacta',
  'direccion',
  'ocupacion',
  'estadoCivil',
];

function fichaCompletitud(form) {
  const filled = TRACK_KEYS.filter((k) => {
    if (k === 'direccion') {
      return hasValue(buildDireccionResumen(form));
    }
    return hasValue(form[k]);
  }).length;
  return Math.round((filled / TRACK_KEYS.length) * 100);
}

function InfoCard({ label, value, icon, highlight }) {
  const empty = !hasValue(value);
  return (
    <div className={`fo-info-card${empty ? ' fo-info-card--empty' : ''}${highlight ? ' fo-info-card--highlight' : ''}`}>
      {icon ? (
        <span className="fo-info-card-icon" aria-hidden>
          <i className={`bi ${icon}`} />
        </span>
      ) : null}
      <div className="fo-info-card-body">
        <span className="fo-info-card-label">{label}</span>
        <span className="fo-info-card-value">{empty ? EMPTY : value}</span>
      </div>
    </div>
  );
}

export function InformacionGeneralSkeleton() {
  return (
    <div className="fo-general fo-general--loading" aria-busy="true" aria-label="Cargando información general">
      <div className="fo-general-header fo-skeleton-block">
        <div className="fo-skeleton fo-skeleton--avatar" />
        <div className="fo-skeleton-lines">
          <div className="fo-skeleton fo-skeleton--title" />
          <div className="fo-skeleton fo-skeleton--chip-row" />
        </div>
        <div className="fo-skeleton fo-skeleton--meter" />
      </div>
      <div className="fo-general-groups">
        {[1, 2, 3].map((g) => (
          <div key={g} className="fo-general-group fo-skeleton-block">
            <div className="fo-skeleton fo-skeleton--group-title" />
            <div className="fo-general-grid">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="fo-skeleton fo-skeleton--card" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function InformacionGeneralPanel({ form, loading = false }) {
  const edad = useMemo(() => calcularEdad(form?.fechaNacimiento), [form?.fechaNacimiento]);
  const direccion = useMemo(() => buildDireccionResumen(form || {}), [form]);
  const contactos = useMemo(() => contactosEmergenciaVista(form || {}), [form]);
  const completitud = useMemo(() => fichaCompletitud(form || {}), [form]);

  if (loading) return <InformacionGeneralSkeleton />;

  const nombre = (form?.nombre || '').trim() || 'Paciente sin nombre';
  const inicial = (nombre[0] || '?').toUpperCase();
  const identificacion = [form?.tipoIdentificacion, form?.cedula].filter(hasValue).join(' · ');

  const chips = [
    identificacion && { icon: 'bi-card-text', text: identificacion },
    edad != null && { icon: 'bi-cake2', text: `${edad} años` },
    hasValue(form?.telefono) && { icon: 'bi-telephone', text: form.telefono },
    hasValue(form?.email) && { icon: 'bi-envelope', text: form.email },
  ].filter(Boolean);

  return (
    <div className="fo-general">
      <div className="fo-general-header">
        <div className="fo-general-avatar" aria-hidden>
          {inicial}
        </div>
        <div className="fo-general-head-text">
          <h3 className="fo-general-name">{nombre}</h3>
          {chips.length > 0 ? (
            <div className="fo-general-chips">
              {chips.map((c, i) => (
                <span key={i} className="fo-general-chip">
                  <i className={`bi ${c.icon}`} />
                  {c.text}
                </span>
              ))}
            </div>
          ) : (
            <p className="fo-general-hint">Completa la ficha para ver un resumen más detallado.</p>
          )}
        </div>
        <div className="fo-general-meter" title="Campos clave de la ficha">
          <span className="fo-general-meter-label">Ficha</span>
          <div className="fo-general-meter-bar" role="progressbar" aria-valuenow={completitud} aria-valuemin={0} aria-valuemax={100}>
            <span className="fo-general-meter-fill" style={{ width: `${completitud}%` }} />
          </div>
          <span className="fo-general-meter-pct">{completitud}%</span>
        </div>
      </div>

      <div className="fo-general-groups">
        <section className="fo-general-group">
          <h4 className="fo-general-group-title">
            <i className="bi bi-person-badge" />
            Identificación
          </h4>
          <div className="fo-general-grid">
            <InfoCard label="Nombre completo" value={form?.nombre} icon="bi-person" highlight />
            <InfoCard label="Tipo de identificación" value={form?.tipoIdentificacion} icon="bi-credit-card" />
            <InfoCard label="Número de identificación" value={form?.cedula} icon="bi-hash" />
            <InfoCard label="Fecha de nacimiento" value={formatFecha(form?.fechaNacimiento)} icon="bi-calendar-event" />
            <InfoCard label="Edad" value={edad != null ? `${edad} años` : ''} icon="bi-cake2" />
            <InfoCard label="Género" value={sexoLabel(form?.sexo)} icon="bi-gender-ambiguous" />
            <InfoCard label="Estado civil" value={form?.estadoCivil} icon="bi-heart" />
            <InfoCard label="Ocupación" value={form?.ocupacion} icon="bi-briefcase" />
          </div>
        </section>

        <section className="fo-general-group">
          <h4 className="fo-general-group-title">
            <i className="bi bi-geo-alt" />
            Ubicación y contacto
          </h4>
          <div className="fo-general-grid">
            <InfoCard label="Provincia" value={form?.direccionProvincia} icon="bi-map" />
            <InfoCard label="Cantón" value={form?.direccionCanton} icon="bi-pin-map" />
            <InfoCard label="Parroquia" value={form?.direccionParroquia} icon="bi-signpost" />
            <InfoCard label="Ciudad" value={form?.direccionCiudad} icon="bi-building" />
            <InfoCard label="Dirección" value={direccion || form?.direccionExacta} icon="bi-house" />
            <InfoCard label="Teléfono" value={form?.telefono} icon="bi-telephone-fill" highlight />
            <InfoCard label="Correo electrónico" value={form?.email} icon="bi-envelope-fill" />
          </div>
        </section>

        {contactos.length > 0 && (
          <section className="fo-general-group">
            <h4 className="fo-general-group-title">
              <i className="bi bi-exclamation-triangle" />
              Contactos de emergencia
            </h4>
            <div className="ficha-emergencia-list">
              {contactos.map((c, i) => (
                <div key={`ce-read-${i}`} className="ficha-emergencia-card ficha-emergencia-card--readonly">
                  <div className="ficha-emergencia-card__head">
                    <span className="ficha-emergencia-card__badge">
                      <i className="bi bi-person-lines-fill" aria-hidden />
                      Contacto {i + 1}
                    </span>
                  </div>
                  <div className="ficha-emergencia-grid">
                    <div>
                      <div className="ficha-emergencia-label">Nombre completo</div>
                      <div className="fo-info-card-value">{c.nombre || EMPTY}</div>
                    </div>
                    <div>
                      <div className="ficha-emergencia-label">Teléfono</div>
                      <div className="fo-info-card-value">
                        {c.telefono ? (
                          <a href={`tel:${c.telefono}`} className="fo-emergencia-tel">{c.telefono}</a>
                        ) : (
                          EMPTY
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="ficha-emergencia-label">Parentesco</div>
                      <div className="fo-info-card-value">{c.relacion || EMPTY}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
